import React, { useContext, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import toast from 'react-hot-toast';
import DContext from '../../context/DContext';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import ContentLoader from '../loader/ContentLoader';
import { flightsService } from '../../services/flights.service';
import { airlinesService } from '../../services/airlines.service';
import { airportsService } from '../../services/airports.service';
import { countriesService } from '../../services/countries.service';
import { citiesService } from '../../services/cities.service';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { formField, formInputStyle, formLabelStyle, formReadOnlyStyle, formSectionCard, formSectionTitle, formSelectStyles } from "../common/FormUI";

// list APIs return either a plain array or { data: [] }
const toArray = (rd) => (Array.isArray(rd) ? rd : rd?.data || []);

const AddFlight = () => {
  const location = useLocation();
  const editFlightId = location.state?.flightId;
  const isEdit = !!editFlightId;

  const { authState, authDispatch, logout } = useContext(DContext);
  const { user } = authState;

  const [loading, setLoading] = useState(false);
  const [airlinesDropdown, setAirlinesDropdown] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [depCityOptions, setDepCityOptions] = useState([]);
  const [arrCityOptions, setArrCityOptions] = useState([]);
  const initialFlightDetail = {
    airlineId: "",
    flightName: "",
    flightCode: "",
    flightNumber: "",
    depAirportId: "",
    depAirportCode: "",
    depAirportLabel: "",
    depCityName: "",
    depCityId: "",
    depCity: "",
    depCountry: "",
    depCountryId: "",
    arrAirportId: "",
    arrAirportCode: "",
    arrAirportLabel: "",
    arrCityName: "",
    arrCityId: "",
    arrCity: "",
    arrCountry: "",
    arrCountryId: "",
    depTime: "",
    arrTime: "",
    depTerminal: "",
    arrTerminal: ""
  };
  const [flightDetail, setFlightDetail] = useState(initialFlightDetail);

  useEffect(() => {
    loadAirlines();
    loadCountries();
    if (isEdit) loadFlightDetail();
  }, [editFlightId]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon })
  }

  const loadFlightDetail = async () => {
    try {
      setLoading(true);
      const res = await flightsService.details(editFlightId);
      if (res?.success) {
        const f = res.resData?.flight ?? res.resData;
        setFlightDetail({
          airlineId: f?.airline?.id || "",
          flightName: f?.flight_name || "",
          flightCode: f?.flight_code || "",
          flightNumber: f?.flight_number || "",
          depAirportId: f?.departureAirport?.id || "",
          depAirportCode: f?.departureAirport?.airport_code || "",
          depAirportLabel: airportLabel(f?.departureAirport),
          depCityName: f?.departureAirport?.city_name || "",
          depCityId: f?.departure_city_id || "",
          depCity: f?.departureCity?.name || "",
          depCountry: f?.departureCountry?.name || "",
          depCountryId: f?.departure_country_id || "",
          arrAirportId: f?.arrivalAirport?.id || "",
          arrAirportCode: f?.arrivalAirport?.airport_code || "",
          arrAirportLabel: airportLabel(f?.arrivalAirport),
          arrCityName: f?.arrivalAirport?.city_name || "",
          arrCityId: f?.arrival_city_id || "",
          arrCity: f?.arrivalCity?.name || "",
          arrCountry: f?.arrivalCountry?.name || "",
          arrCountryId: f?.arrival_country_id || "",
          depTime: (f?.departure_time || "").slice(0, 5),
          arrTime: (f?.arrival_time || "").slice(0, 5),
          depTerminal: f?.departure_terminal || "",
          arrTerminal: f?.arrival_terminal || ""
        });
        loadCities("dep", f?.departure_country_id);
        loadCities("arr", f?.arrival_country_id);
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  // Airline dropdown: airlines > list (status = active)
  const loadAirlines = async () => {
    try {
      const res = await airlinesService.list({ page: 1, status: "active" });
      if (res?.success) {
        setAirlinesDropdown(toArray(res.resData).map(a => ({
          value: a.id, label: a.airline_name, airline_name: a.airline_name, airline_code: a.airline_code
        })));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  // Same text for the dropdown option and for the selected value
  const airportLabel = (a) => (a?.airport_name ? `${a.airport_name} (${a.airport_code}), ${a.city_name}` : "");

  const searchAirports = async (extra) => {
    const res = await airportsService.list({ page: 1, limit: 50, status: "active", ...extra });
    return res?.success ? toArray(res.resData) : [];
  }

  // Departure / Arrival airport dropdowns: airports > list (status = active), searched on the server
  // by airport name or IATA / airport code (both searched, results merged)
  const loadAirports = async (search = "") => {
    try {
      const term = search.trim();
      const lists = term
        ? await Promise.all([searchAirports({ search: term }), searchAirports({ airport_name: term }), searchAirports({ airport_code: term })])
        : [await searchAirports({})];
      const unique = new Map();
      lists.flat().forEach((a) => unique.set(a.id, a));
      return [...unique.values()].map(a => ({
        value: a.id,
        label: airportLabel(a),
        airport_code: a.airport_code,
        city_name: a.city_name,
        country_name: a.country_name
      }));
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
      return [];
    }
  }
  // Country dropdown: countries > list
  const loadCountries = async () => {
    try {
      const res = await countriesService.list({ page: 1, status: "active" });
      if (res?.success) {
        setCountryOptions((res.resData?.data || []).map(c => ({ value: c.id, label: c.name })));
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  // City dropdown: cities > list based on the selected country
  const loadCities = async (side, countryId) => {
    const setter = side === "dep" ? setDepCityOptions : setArrCityOptions;
    if (!countryId) { setter([]); return; }
    try {
      const res = await citiesService.list({ page: 1, status: "active", country_id: Number(countryId) });
      if (res?.success) {
        setter((res.resData?.data || []).map(c => ({ value: c.id, label: c.name })));
      } else {
        setter([]);
        toast.error(res?.message);
      }
    } catch (err) {
      setter([]);
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    }
  }

  const handleSelectChange = (selectedOption) => {
    setFlightDetail({
      ...flightDetail,
      flightName: selectedOption?.airline_name || "",
      airlineId: selectedOption?.value || "",
      flightCode: selectedOption?.airline_code || ""
    })
  };

  // side = "dep" | "arr": copies the airport and syncs the country / city dropdowns from it
  const handleAirportChange = (side, selectedOption) => {
    const countryName = selectedOption?.country_name || "";
    const match = countryOptions.find(c => c.label.toLowerCase() === countryName.toLowerCase());
    setFlightDetail({
      ...flightDetail,
      [side + "AirportId"]: selectedOption?.value || "",
      [side + "AirportCode"]: selectedOption?.airport_code || "",
      [side + "AirportLabel"]: selectedOption?.label || "",
      [side + "CityName"]: selectedOption?.city_name || "",
      [side + "Country"]: countryName,
      [side + "CountryId"]: match?.value || "",
      [side + "CityId"]: "",
      [side + "City"]: ""
    });
    loadCities(side, match?.value);
  };

  const handleCountryChange = (side, selectedOption) => {
    setFlightDetail({
      ...flightDetail,
      [side + "CountryId"]: selectedOption?.value || "",
      [side + "Country"]: selectedOption?.label || "",
      [side + "CityId"]: "",
      [side + "City"]: ""
    });
    loadCities(side, selectedOption?.value);
  };

  const handleCityChange = (side, selectedOption) => {
    setFlightDetail({
      ...flightDetail,
      [side + "CityId"]: selectedOption?.value || "",
      [side + "City"]: selectedOption?.label || ""
    });
  };

  const handleChange = (e) => {
    setFlightDetail({ ...flightDetail, [e.target.name]: e.target.value });
  }

  const handleTimeChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // remove non-digits

    if (value.length > 2) {
      value = value.slice(0, 2) + ":" + value.slice(2, 4); // insert ":"
    }

    if (value.length > 5) {
      value = value.slice(0, 5); // max length 5 (HH:MM)
    }

    setFlightDetail({ ...flightDetail, [e.target.name]: value });
  };

  // Add: clears the form. Update: puts back the saved flight details.
  const handleReset = () => {
    if (isEdit) {
      loadFlightDetail();
      return;
    }
    setFlightDetail(initialFlightDetail);
    setDepCityOptions([]);
    setArrCityOptions([]);
  };

  // Terminal is optional: only one digit (0-9) allowed
  const handleTerminalChange = (e) => {
    setFlightDetail({ ...flightDetail, [e.target.name]: e.target.value.replace(/\D/g, "").slice(0, 1) });
  };

  const validate = () => {
    if (!flightDetail.airlineId) return "Please select an airline";
    if (!flightDetail.flightCode) return "Flight code is required";
    if (!String(flightDetail.flightNumber).trim()) return "Flight number is required";
    if (!flightDetail.depAirportId) return "Please select a departure airport";
    if (!flightDetail.depCountryId) return "Please select a departure country";
    if (!flightDetail.depCityId) return "Please select a departure city";
    if (!flightDetail.arrAirportId) return "Please select an arrival airport";
    if (!flightDetail.arrCountryId) return "Please select an arrival country";
    if (!flightDetail.arrCityId) return "Please select an arrival city";
    if (flightDetail.depAirportId === flightDetail.arrAirportId) return "Departure and arrival airport cannot be the same";
    if (!/^\d{2}:\d{2}$/.test(flightDetail.depTime)) return "Enter departure time as HH:MM";
    if (!/^\d{2}:\d{2}$/.test(flightDetail.arrTime)) return "Enter arrival time as HH:MM";
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const err = validate();
      if (err) { toast.error(err); return; }
      setLoading(true);

      const payload = {
        airline_code: flightDetail.flightCode,
        departure_airport_code: flightDetail.depAirportCode,
        arrival_airport_code: flightDetail.arrAirportCode,
        departure_country_id: Number(flightDetail.depCountryId) || undefined,
        arrival_country_id: Number(flightDetail.arrCountryId) || undefined,
        departure_city_id: Number(flightDetail.depCityId) || null,
        arrival_city_id: Number(flightDetail.arrCityId) || null,
        flight_code: flightDetail.flightCode,
        flight_name: flightDetail.flightName,
        flight_number: String(flightDetail.flightNumber),
        departure_time: flightDetail.depTime,
        arrival_time: flightDetail.arrTime,
        departure_terminal: flightDetail.depTerminal,
        arrival_terminal: flightDetail.arrTerminal,
      };
      const res = isEdit
        ? await flightsService.update(editFlightId, payload)
        : await flightsService.create({ ...payload, status: "active" });
      if (res?.success) {
        showSwal("Success", res.message, "success");
        if (!isEdit) {
          setFlightDetail(initialFlightDetail);
          setDepCityOptions([]);
          setArrCityOptions([]);
        }
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error")
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (<ContentLoader loaderType="circleSpin" />)

  const GRAD = "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)";
  const labelStyle = formLabelStyle;
  const inputStyle = formInputStyle;
  const readOnlyStyle = formReadOnlyStyle;
  const selectStyles = formSelectStyles;
  const sectionCard = formSectionCard;
  const sectionTitle = formSectionTitle;
  const field = (label, required, node, cls = "col-12 col-md-6 col-lg-4") => formField(label, required, node, cls);
  const QUARTER = "col-12 col-md-6 col-lg-3"; // 4 fields per row
  const title = isEdit ? "Update Flight" : "Add Flight";

  return (
    <>
      <section className="section dashboard">
        {/* Page header */}
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>{isEdit ? "Update Flight" : "Add New Flight"}</h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <Link to="/flight/list" style={{ color: "#475569" }}>Flights</Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>{title}</span>
            </nav>
          </div>
          <Link to="/flight/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
            style={{ background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 }}>
            <i className="bi bi-list-ul"></i> Flights List
          </Link>
        </div>

        <form className="v2a-form" onSubmit={handleSubmit}>

            {/* One compact box: airline, departure, arrival, schedule */}
            <div style={sectionCard}>
              <div className="row g-3">
                {/* Airline */}
                {field("Airline", true,
                  <Select
                    options={airlinesDropdown}
                    value={flightDetail.airlineId ? { value: flightDetail.airlineId, label: flightDetail.flightName } : null}
                    onChange={handleSelectChange}
                    placeholder="Select airline"
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Flight Code", true,
                  <div className="d-flex align-items-center gap-2">
                    <input type="text" className="form-control" placeholder="Auto-filled from airline" name="flightCode"
                      value={flightDetail?.flightCode || ""} readOnly style={{ ...readOnlyStyle, textTransform: "uppercase" }} />
                    {flightDetail?.flightCode && (
                      <img src={`https://images.kiwi.com/airlines/32/${flightDetail?.flightCode}.png`}
                        style={{ width: 28, height: 28, objectFit: "contain" }} alt="airline logo" />
                    )}
                  </div>
                )}
                {field("Flight Number", true,
                  <input type="number" min="0" className="form-control" placeholder="e.g. 202" name="flightNumber"
                    value={flightDetail?.flightNumber || ""} onChange={handleChange} style={inputStyle} />
                )}

                {/* Departure (the airport code is part of the selected airport label) */}
                {field("Departure Airport", true,
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    name="dep_airport_dropdown"
                    value={flightDetail.depAirportId ? { value: flightDetail.depAirportId, label: flightDetail.depAirportLabel || flightDetail.depAirportCode } : null}
                    placeholder="Search departure airport"
                    loadOptions={loadAirports}
                    onChange={(o) => handleAirportChange("dep", o)}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Departure Country", true,
                  <Select
                    options={countryOptions}
                    value={flightDetail.depCountryId ? { value: flightDetail.depCountryId, label: flightDetail.depCountry } : null}
                    onChange={(o) => handleCountryChange("dep", o)}
                    placeholder="Select country"
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Departure City", true,
                  <Select
                    options={depCityOptions}
                    isDisabled={!flightDetail.depCountryId}
                    value={flightDetail.depCityId ? { value: flightDetail.depCityId, label: flightDetail.depCity } : null}
                    onChange={(o) => handleCityChange("dep", o)}
                    placeholder={flightDetail.depCountryId ? "Select city" : "Select country first"}
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}

                {/* Arrival */}
                {field("Arrival Airport", true,
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    name="arr_airport_dropdown"
                    value={flightDetail.arrAirportId ? { value: flightDetail.arrAirportId, label: flightDetail.arrAirportLabel || flightDetail.arrAirportCode } : null}
                    placeholder="Search arrival airport"
                    loadOptions={loadAirports}
                    onChange={(o) => handleAirportChange("arr", o)}
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Arrival Country", true,
                  <Select
                    options={countryOptions}
                    value={flightDetail.arrCountryId ? { value: flightDetail.arrCountryId, label: flightDetail.arrCountry } : null}
                    onChange={(o) => handleCountryChange("arr", o)}
                    placeholder="Select country"
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}
                {field("Arrival City", true,
                  <Select
                    options={arrCityOptions}
                    isDisabled={!flightDetail.arrCountryId}
                    value={flightDetail.arrCityId ? { value: flightDetail.arrCityId, label: flightDetail.arrCity } : null}
                    onChange={(o) => handleCityChange("arr", o)}
                    placeholder={flightDetail.arrCountryId ? "Select city" : "Select country first"}
                    isSearchable
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                )}

                {/* Schedule & terminals (times are 24-hour HH:MM), 4 per row */}
                {field("Departure Time", true,
                  <input type="text" className="form-control" placeholder="HH:MM" name="depTime" maxLength={5}
                    value={flightDetail?.depTime || ""} onChange={handleTimeChange} style={inputStyle} />,
                  QUARTER
                )}
                {field("Departure Terminal", false,
                  <input type="text" className="form-control" placeholder="e.g. 1" name="depTerminal" maxLength={1} inputMode="numeric"
                    value={flightDetail?.depTerminal || ""} onChange={handleTerminalChange} style={inputStyle} />,
                  QUARTER
                )}
                {field("Arrival Time", true,
                  <input type="text" className="form-control" placeholder="HH:MM" name="arrTime" maxLength={5}
                    value={flightDetail?.arrTime || ""} onChange={handleTimeChange} style={inputStyle} />,
                  QUARTER
                )}
                {field("Arrival Terminal", false,
                  <input type="text" className="form-control" placeholder="e.g. 2" name="arrTerminal" maxLength={1} inputMode="numeric"
                    value={flightDetail?.arrTerminal || ""} onChange={handleTerminalChange} style={inputStyle} />,
                  QUARTER
                )}
              </div>
            </div>

            {/* Footer buttons */}
            <div className="d-flex justify-content-end gap-2">
              {/* <Link to="/flight/list" className="btn px-4 d-flex align-items-center"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                Back
              </Link> */}
              <button type="button" onClick={handleReset} className="btn px-4 d-flex align-items-center gap-1"
                style={{ background: "#fff", border: "1px solid #c3cad6", borderRadius: 8, height: 40, fontSize: 13, color: "#444" }}>
                <i className="bi bi-arrow-counterclockwise"></i> Reset Fields
              </button>
              <button type="submit" className="btn px-4 text-white fw-semibold"
                style={{ background: GRAD, border: "none", borderRadius: 8, height: 40, fontSize: 13 }}>
                {isEdit ? "Update Flight" : "Add Flight"}
              </button>
            </div>

        </form>
      </section>
    </>
  );
}

export default AddFlight;
